import { computed, inject, ref, useSlots, toRef, watch, openBlock, createElementBlock, toDisplayString, createCommentVNode, renderSlot, createVNode, withCtx, Fragment, renderList, createElementVNode, normalizeClass, createTextVNode, createBlock, unref } from "vue";
import UikitInputNew from "./InputNew.vue.es.js";
import UikitDropdown from "../Dropdown.vue.es.js";
import UikitSkeleton from "../Skeleton.vue.es.js";
import UikitChevronIcon from "../../uikit.icons/ChevronIcon.vue.es.js";
import WarningIcon from "../../uikit.icons/WarningIcon.vue.es.js";
import ErrorIcon from "../../uikit.icons/ErrorIcon.vue.es.js";
import Loader from "../Loader.vue.es.js";
import { useField } from "vee-validate";
/* empty css                    */
import _export_sfc from "../../uikit._virtual/_plugin-vue_export-helper.es.js";
const _hoisted_1 = ["id"];
const _hoisted_2 = {
  key: 0,
  class: "uikit-input-select-label"
};
const _hoisted_3 = { key: 0 };
const _hoisted_4 = {
  key: 1,
  class: "label-suffix"
};
const _hoisted_5 = {
  key: 0,
  class: "prefix icon"
};
const _hoisted_6 = ["value"];
const _hoisted_7 = {
  key: 1,
  class: "status-icon"
};
const _hoisted_8 = { class: "chevron-icon icon" };
const _hoisted_9 = {
  key: 0,
  class: "uikit-input-select-search"
};
const _hoisted_10 = { class: "uikit-input-select-category" };
const _hoisted_11 = {
  key: 0,
  class: "uikit-input-select-container"
};
const _hoisted_12 = ["id", "onClick"];
const _hoisted_13 = {
  key: 0,
  class: "separator"
};
const _hoisted_14 = ["id", "onClick"];
const _hoisted_15 = {
  key: 1,
  class: "uikit-input-error"
};
const _hoisted_16 = {
  key: 2,
  class: "uikit-input-warning"
};
const _sfc_main = {
  __name: "InputSelect",
  props: {
    id: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      default: ""
    },
    rules: {
      type: [String, Function],
      required: false,
      default: void 0
    },
    modelValue: {
      type: [Number, String, Array],
      default: void 0
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: "Select"
    },
    closeOnSelection: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    skeleton: {
      type: Boolean,
      default: false
    },
    syncVModel: {
      type: Boolean,
      default: true
    },
    standalone: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    canUnselect: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    valueKey: {
      type: String,
      default: "value"
    },
    labelKey: {
      type: String,
      default: "label"
    },
    allowSearch: {
      type: Boolean,
      default: false
    },
    dropDownClass: {
      type: String,
      default: ""
    }
  },
  setup(__props) {
    const INPUT_STATUS = {
      NONE: "none",
      ERROR: "error",
      WARNING: "warning",
      LOADING: "loading"
    };
    const status = computed(() => {
      if (errorMessage.value) {
        return INPUT_STATUS.ERROR;
      } else if (slots.warning) {
        return INPUT_STATUS.WARNING;
      } else {
        return INPUT_STATUS.NONE;
      }
    });
    const isFormDisabled = inject("isFormDisabled", false);
    const isFormSkeleton = inject("isFormSkeleton", false);
    const displaySkeleton = computed(() => isFormSkeleton.value || props.skeleton);
    const search = ref("");
    const optionsToDisplay = computed(() => {
      if (search.value) {
        return props.options.filter(
          (option) => option[props.labelKey].toLowerCase().includes(search.value.toLowerCase())
        );
      } else {
        return props.options;
      }
    });
    const isError = computed(() => status.value === INPUT_STATUS.ERROR);
    const isWarning = computed(() => status.value === INPUT_STATUS.WARNING);
    const isDisabled = computed(() => isFormDisabled.value || props.disabled);
    const isLoading = computed(() => props.loading);
    const hasStatusIcon = computed(() => isError.value || isLoading.value);
    const inputWrapperClasses = computed(() => ({
      "uikit-input-wrapper-error": isError.value,
      "uikit-input-wrapper-warning": isWarning.value,
      "uikit-input-wrapper-disabled": isDisabled.value
    }));
    const props = __props;
    const slots = useSlots();
    const hasPrefix = computed(() => !!slots.prefix);
    const hasLabelSuffix = computed(() => !!slots["label-suffix"]);
    const isOpen = ref(false);
    const displayLabel = computed(() => props.label !== "");
    const name = toRef(props, "name");
    const { value, handleChange, meta, errorMessage, validate } = useField(name, props.rules, {
      initialValue: props.modelValue,
      syncVModel: props.syncVModel,
      standalone: props.standalone
    });
    const selected = computed(() => {
      if (Array.isArray(value.value)) {
        return value.value;
      } else if (typeof value.value !== "undefined" && value.value !== null) {
        return [value.value];
      } else {
        return [];
      }
    });
    const currentSelectedOption = computed(() => {
      if (selected.value.length > 0) {
        return props.options.find((option) => selected.value[0] === option[props.valueKey]);
      }
      return null;
    });
    const currentSelected = computed(() => {
      if (selected.value.length > 0) {
        let labels = [];
        for (let i = 0; i < selected.value.length; i++) {
          const selectedOption = props.options.find((option) => selected.value[i] === option[props.valueKey]);
          if (selectedOption) {
            labels.push(selectedOption[props.labelKey]);
          }
        }
        if (labels.length > 0) {
          return labels.join(", ");
        }
      }
      return null;
    });
    const placeholder = computed(() => {
      if (currentSelected.value) {
        return currentSelected.value;
      } else {
        return props.placeholder;
      }
    });
    const isSelected = (option) => {
      return selected.value.some((el) => el === option);
    };
    const handleSelect = (option) => {
      if (!isDisabled.value) {
        let selectedOptions = selected.value;
        if (props.multiple) {
          if (isSelected(option)) {
            selectedOptions.splice(selected.value.indexOf(option), 1);
          } else {
            selectedOptions.push(option);
          }
        } else {
          if (props.canUnselect && selectedOptions.length > 0 && selectedOptions[0] === option) {
            selectedOptions = [];
          } else {
            selectedOptions = [option];
          }
        }
        handleChange(props.multiple ? selectedOptions : selectedOptions[0] ?? null);
        if (!meta.valid) {
          validate();
        }
      }
      isOpen.value = !props.closeOnSelection;
      setTimeout(() => search.value = "", 200);
    };
    const handleDropdownClose = () => {
      if (!meta.valid) {
        validate();
      }
    };
    const hasCategories = computed(() => props.options.some((el) => el.category));
    const categories = computed(() => {
      const categories2 = props.options.map((option) => option.category);
      return [...new Set(categories2)];
    });
    watch(
      () => props.options,
      () => {
        if (selected.value[0] !== null && selected.value[0] !== void 0 && !currentSelected.value) {
          handleChange(null);
        }
      }
    );
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        id: __props.id,
        class: "uikit-input uikit-input-select"
      }, [
        displayLabel.value ? (openBlock(), createElementBlock("span", _hoisted_2, [
          __props.label ? (openBlock(), createElementBlock("label", _hoisted_3, toDisplayString(__props.label), 1)) : createCommentVNode("", true),
          hasLabelSuffix.value ? (openBlock(), createElementBlock("span", _hoisted_4, [
            renderSlot(_ctx.$slots, "label-suffix", {}, void 0, true)
          ])) : createCommentVNode("", true)
        ])) : createCommentVNode("", true),
        createVNode(UikitDropdown, {
          modelValue: isOpen.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => isOpen.value = $event),
          trigger: "click",
          "drop-down-class": __props.dropDownClass,
          "force-close-on-click": true,
          onOnClose: handleDropdownClose
        }, {
          overlay: withCtx(() => [
            __props.allowSearch ? (openBlock(), createElementBlock("div", _hoisted_9, [
              createVNode(UikitInputNew, {
                id: `${__props.id}-option-search`,
                modelValue: search.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => search.value = $event),
                name: `${name.value}-option-search`
              }, null, 8, ["id", "modelValue", "name"])
            ])) : createCommentVNode("", true),
            hasCategories.value ? (openBlock(true), createElementBlock(Fragment, { key: 1 }, renderList(categories.value, (category, index) => {
              return openBlock(), createElementBlock("div", { key: category }, [
                createElementVNode("div", _hoisted_10, toDisplayString(category), 1),
                (openBlock(true), createElementBlock(Fragment, null, renderList(__props.options, (option, index2) => {
                  return openBlock(), createElementBlock(Fragment, {
                    key: option[__props.labelKey] + "-" + index2
                  }, [
                    option.category === category ? (openBlock(), createElementBlock("div", _hoisted_11, [
                      createElementVNode("div", {
                        id: option[__props.valueKey],
                        class: normalizeClass(["uikit-input-select-item", { selected: isSelected(option[__props.valueKey]) }]),
                        onClick: ($event) => handleSelect(option[__props.valueKey])
                      }, toDisplayString(option[__props.labelKey]), 11, _hoisted_12)
                    ])) : createCommentVNode("", true)
                  ], 64);
                }), 128)),
                index < categories.value.length - 1 ? (openBlock(), createElementBlock("div", _hoisted_13)) : createCommentVNode("", true)
              ]);
            }), 128)) : (openBlock(true), createElementBlock(Fragment, { key: 2 }, renderList(optionsToDisplay.value, (option, index) => {
              return openBlock(), createElementBlock("div", {
                key: option[__props.labelKey] + "-" + index,
                class: "uikit-input-select-container no-category"
              }, [
                createElementVNode("div", {
                  id: option[__props.valueKey],
                  class: normalizeClass(["uikit-input-select-item", { selected: isSelected(option[__props.valueKey]) }]),
                  onClick: ($event) => handleSelect(option[__props.valueKey])
                }, [
                  renderSlot(_ctx.$slots, "item", { item: option }, () => [
                    createTextVNode(toDisplayString(option[__props.labelKey]), 1)
                  ], true)
                ], 10, _hoisted_14)
              ]);
            }), 128))
          ]),
          default: withCtx(() => [
            createVNode(UikitSkeleton, {
              type: "input",
              loading: displaySkeleton.value
            }, {
              default: withCtx(() => [
                createElementVNode("div", {
                  class: normalizeClass(["uikit-input-wrapper uikit-input-select-wrapper", inputWrapperClasses.value])
                }, [
                  hasPrefix.value && !__props.loading ? (openBlock(), createElementBlock("span", _hoisted_5, [
                    renderSlot(_ctx.$slots, "prefix", {}, void 0, true)
                  ])) : createCommentVNode("", true),
                  renderSlot(_ctx.$slots, "input", { item: currentSelectedOption.value }, () => [
                    createElementVNode("input", {
                      class: "placeholder",
                      disabled: "",
                      value: placeholder.value
                    }, null, 8, _hoisted_6)
                  ], true),
                  hasStatusIcon.value ? (openBlock(), createElementBlock("span", _hoisted_7, [
                    isError.value ? (openBlock(), createBlock(ErrorIcon, {
                      key: 0,
                      class: "error-icon",
                      full: true
                    })) : isLoading.value ? (openBlock(), createBlock(Loader, {
                      key: 1,
                      show: true,
                      transparent: true,
                      width: "20px",
                      height: "20px",
                      class: "loading-icon"
                    })) : createCommentVNode("", true)
                  ])) : createCommentVNode("", true),
                  createElementVNode("span", _hoisted_8, [
                    createVNode(UikitChevronIcon, { type: "down" })
                  ])
                ], 2)
              ]),
              _: 3
            }, 8, ["loading"])
          ]),
          _: 3
        }, 8, ["modelValue", "drop-down-class"]),
        unref(errorMessage) ? (openBlock(), createElementBlock("div", _hoisted_15, toDisplayString(unref(errorMessage)), 1)) : isWarning.value ? (openBlock(), createElementBlock("div", _hoisted_16, [
          createVNode(WarningIcon, { class: "warning-icon" }),
          renderSlot(_ctx.$slots, "warning", {}, void 0, true)
        ])) : createCommentVNode("", true)
      ], 8, _hoisted_1);
    };
  }
};
const UikitInputSelect = /* @__PURE__ */ _export_sfc(_sfc_main, [["__scopeId", "data-v-40c262ad"]]);
export {
  UikitInputSelect as default
};
//# sourceMappingURL=InputSelect.vue.es.js.map
